import type { FileNode, FilePolicy } from '@/features/files/types';
import type { ProjectScope } from '@/features/sync/project-scope';

export interface CreateUploadSessionRequest {
  fileName: string;
  contentType: string;
  byteSize: number;
  scope: ProjectScope;
}

export interface BlobUploadTarget {
  id: string;
  storagePath: string;
  contentType?: string;
  customMetadata?: Record<string, string>;
}

export interface UploadSession extends BlobUploadTarget {
  fileId?: string;
  versionNumber?: number;
}

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
}

export interface UploadTask {
  cancel(): void;
  finished: Promise<void>;
  onProgress(listener: (progress: UploadProgress) => void): () => void;
}

export interface FinalizeUploadRequest {
  sessionId: string;
  fileId: string;
  storagePath: string;
  versionNumber: number;
  parentId: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  policy: FilePolicy;
  scope: ProjectScope;
}

export type FileRecord = FileNode;

export interface FileAccessRequest {
  fileId: string;
  action?: 'access' | 'preview' | 'download';
  reason?: string;
  scope: ProjectScope;
  storageRef?: string;
}

export interface FileAccessUrl {
  url: string;
  expiresAt: number;
  file?: FileNode;
  contentType?: string;
}

export interface BlobReadRequest {
  storagePath: string;
}

export interface BlobDownloadUrlRequest {
  storagePath: string;
}

export interface BlobListRequest {
  prefix: string;
}

export interface BlobCleanupRequest {
  storagePath: string;
  ignoreMissing?: boolean;
}

export class BlobObjectNotFoundError extends Error {
  readonly storagePath: string;

  constructor(storagePath: string) {
    super(`Blob object not found: ${storagePath}`);
    this.name = 'BlobObjectNotFoundError';
    this.storagePath = storagePath;
  }
}

export function isBlobObjectNotFoundError(error: unknown): error is BlobObjectNotFoundError {
  return error instanceof BlobObjectNotFoundError;
}

export interface BlobStoreClient {
  createUploadSession(request: CreateUploadSessionRequest): Promise<UploadSession>;
  upload(target: BlobUploadTarget, file: File, options?: { signal?: AbortSignal }): UploadTask;
  finalizeUpload(request: FinalizeUploadRequest): Promise<FileRecord>;
  getAccessUrl(request: FileAccessRequest): Promise<FileAccessUrl>;
  getBlob(request: BlobReadRequest): Promise<Blob>;
  getDownloadUrl(request: BlobDownloadUrlRequest): Promise<string>;
  list(request: BlobListRequest): Promise<string[]>;
  cleanup(request: BlobCleanupRequest): Promise<void>;
}
